import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Trash2, Plus, Target, BookOpen, Users, CheckCircle, AlertCircle, Monitor, Settings, ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson, TimelineStep, StepResource, Resource } from '../../../types';
import { ResourceConfigModal } from './ResourceConfigModal';
import { RichTextEditor } from '../../Common/RichTextEditor';

interface TimelineStepsSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
  errors: Record<string, string>;
  onSave: (e: React.FormEvent) => Promise<string | null>;
}

export const TimelineStepsSection: React.FC<TimelineStepsSectionProps> = ({
  formData,
  setFormData,
  onSave
}) => {
  const { t } = useTranslation();
  
  // Modal state
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<{ 
    stepIndex: number; 
    resourceIndex: number; 
    resource: Resource | null; 
    currentConfig?: Record<string, any>;
  } | null>(null);
  
  // Collapsed steps state - track which steps are collapsed
  const [collapsedSteps, setCollapsedSteps] = useState<Set<string>>(new Set());
  
  const toggleStepCollapse = (stepId: string) => {
    setCollapsedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  };
  
  const isStepCollapsed = (stepId: string) => {
    return collapsedSteps.has(stepId);
  };
  
  const stepTypes = [
    { value: 'warmup', label: t('timelineSteps.warmUp'), icon: Play },
    { value: 'launch', label: t('timelineSteps.launch'), icon: Target },
    { value: 'direct_instruction', label: t('timelineSteps.directInstruction'), icon: BookOpen },
    { value: 'guided_practice', label: t('timelineSteps.guidedPractice'), icon: Users },
    { value: 'independent_practice', label: t('timelineSteps.independentPractice'), icon: CheckCircle },
    { value: 'closing', label: t('timelineSteps.closing'), icon: AlertCircle }
  ];

  const addTimelineStep = () => {
    const newStep: TimelineStep = {
      id: `step_${Date.now()}`,
      name: '',
      minutes: 5,
      step_type: 'warmup',
      teacher_script: [''],
      student_actions: [''],
      checks_for_understanding: [],
      resources: []
    };

    setFormData(prev => ({
      ...prev,
      timeline: [...(prev.timeline || []), newStep]
    }));
  };

  const removeTimelineStep = (index: number) => {
    const step = formData.timeline?.[index];
    const stepId = step?.id || `step-${index}`;
    
    // Remove from collapsed steps if it was collapsed
    setCollapsedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(stepId);
      return newSet;
    });
    
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.filter((_, i) => i !== index) || []
    }));
  };

  const moveStepUp = (index: number) => {
    if (index === 0) return; // Already at the top
    
    setFormData(prev => {
      const timeline = [...(prev.timeline || [])];
      const temp = timeline[index];
      timeline[index] = timeline[index - 1];
      timeline[index - 1] = temp;
      return {
        ...prev,
        timeline
      };
    });
  };

  const moveStepDown = (index: number) => {
    const timeline = formData.timeline || [];
    if (index === timeline.length - 1) return; // Already at the bottom
    
    setFormData(prev => {
      const timeline = [...(prev.timeline || [])];
      const temp = timeline[index];
      timeline[index] = timeline[index + 1];
      timeline[index + 1] = temp;
      return {
        ...prev,
        timeline
      };
    });
  };

  const updateTimelineStep = (index: number, field: keyof TimelineStep, value: any) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      ) || []
    }));
  };

  const addTeacherScript = (stepIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { ...step, teacher_script: [...(step.teacher_script || []), ''] }
          : step
      ) || []
    }));
  };

  const updateTeacherScript = (stepIndex: number, scriptIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              teacher_script: step.teacher_script?.map((script, j) => 
                j === scriptIndex ? value : script
              ) || []
            }
          : step
      ) || []
    }));
  };

  const removeTeacherScript = (stepIndex: number, scriptIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              teacher_script: step.teacher_script?.filter((_, j) => j !== scriptIndex) || []
            }
          : step
      ) || []
    }));
  };

  const addStudentAction = (stepIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { ...step, student_actions: [...(step.student_actions || []), ''] }
          : step
      ) || []
    }));
  };

  const updateStudentAction = (stepIndex: number, actionIndex: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              student_actions: step.student_actions?.map((action, j) => 
                j === actionIndex ? value : action
              ) || []
            }
          : step
      ) || []
    }));
  };

  const removeStudentAction = (stepIndex: number, actionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              student_actions: step.student_actions?.filter((_, j) => j !== actionIndex) || []
            }
          : step
      ) || []
    }));
  };

  const addCheckForUnderstanding = (stepIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { ...step, checks_for_understanding: [...(step.checks_for_understanding || []), { type: 'Observation', prompt: '', look_for: '' }] }
          : step
      ) || []
    }));
  };

  const updateCheckForUnderstanding = (stepIndex: number, checkIndex: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              checks_for_understanding: step.checks_for_understanding?.map((check, j) => 
                j === checkIndex ? { ...check, [field]: value } : check
              ) || []
            }
          : step
      ) || []
    }));
  };

  const removeCheckForUnderstanding = (stepIndex: number, checkIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              checks_for_understanding: step.checks_for_understanding?.filter((_, j) => j !== checkIndex) || []
            }
          : step
      ) || []
    }));
  };

  // Resource reference management functions
  const addResourceReference = (stepIndex: number, resourceId: string) => {
    const stepResource: StepResource = { resourceId };
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { ...step, resources: [...(step.resources || []), stepResource] }
          : step
      ) || []
    }));
  };

  const removeResourceReference = (stepIndex: number, resourceIndex: number) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              resources: step.resources?.filter((_, j) => j !== resourceIndex) || []
            }
          : step
      ) || []
    }));
  };

  const updateResourceConfig = (stepIndex: number, resourceIndex: number, config: Record<string, any>) => {
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? { 
              ...step, 
              resources: step.resources?.map((resource, j) => 
                j === resourceIndex 
                  ? { ...resource, config }
                  : resource
              ) || []
            }
          : step
      ) || []
    }));
  };

  const moveResourceUp = (stepIndex: number, resourceIndex: number) => {
    if (resourceIndex === 0) return; // Already at the top
    
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? {
              ...step,
              resources: (() => {
                const resources = [...(step.resources || [])];
                const temp = resources[resourceIndex];
                resources[resourceIndex] = resources[resourceIndex - 1];
                resources[resourceIndex - 1] = temp;
                return resources;
              })()
            }
          : step
      ) || []
    }));
  };

  const moveResourceDown = (stepIndex: number, resourceIndex: number) => {
    const step = formData.timeline?.[stepIndex];
    const resources = step?.resources || [];
    if (resourceIndex === resources.length - 1) return; // Already at the bottom
    
    setFormData(prev => ({
      ...prev,
      timeline: prev.timeline?.map((step, i) => 
        i === stepIndex 
          ? {
              ...step,
              resources: (() => {
                const resources = [...(step.resources || [])];
                const temp = resources[resourceIndex];
                resources[resourceIndex] = resources[resourceIndex + 1];
                resources[resourceIndex + 1] = temp;
                return resources;
              })()
            }
          : step
      ) || []
    }));
  };

  // Modal handlers
  const openConfigModal = (stepIndex: number, resourceIndex: number) => {
    const step = formData.timeline?.[stepIndex];
    const stepResource = step?.resources?.[resourceIndex];
    const resource = stepResource ? formData.resources?.[stepResource.resourceId] : null;
    
    if (resource && stepResource) {
      setSelectedResource({
        stepIndex,
        resourceIndex,
        resource,
        currentConfig: stepResource.config || {}
      });
      setIsConfigModalOpen(true);
    }
  };

  const closeConfigModal = () => {
    setIsConfigModalOpen(false);
    setSelectedResource(null);
  };

  const handleConfigSave = (config: Record<string, any>) => {
    if (selectedResource) {
      updateResourceConfig(selectedResource.stepIndex, selectedResource.resourceIndex, config);
      // onSave(new Event('submit') as unknown as React.FormEvent);
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Play className="w-5 h-5 mr-2" />
        {t('timelineSteps.timelineSteps')}
      </h2>
      
      <div className="space-y-6">
        {formData.timeline?.map((step, index) => (
          <motion.div
            key={step.id || `step-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-gray-200 rounded-lg p-4 bg-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2 flex-1">
                {/* Collapse/Expand Button */}
                <button
                  type="button"
                  onClick={() => toggleStepCollapse(step.id || `step-${index}`)}
                  className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title={isStepCollapsed(step.id || `step-${index}`) ? t('timelineSteps.expandStep') : t('timelineSteps.collapseStep')}
                >
                  {isStepCollapsed(step.id || `step-${index}`) ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
              <h3 className="text-md font-medium text-gray-900">
                {t('timelineSteps.step')} {index + 1}
                  {step.name && (
                    <span className="ml-2 text-sm font-normal text-gray-600">
                      - {step.name}
                    </span>
                  )}
              </h3>
                
                {/* Summary when collapsed */}
                {isStepCollapsed(step.id || `step-${index}`) && (
                  <div className="ml-4 flex items-center gap-3 text-xs text-gray-500">
                    <span>{stepTypes.find(t => t.value === step.step_type)?.label || step.step_type}</span>
                    <span>•</span>
                    <span>{step.minutes} {t('timelineSteps.minutes')}</span>
                    {step.resources && step.resources.length > 0 && (
                      <>
                        <span>•</span>
                        <span>{step.resources.length} {t('timelineSteps.resources')}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-1">
                {/* Move Up Button */}
                <button
                  type="button"
                  onClick={() => moveStepUp(index)}
                  disabled={index === 0}
                  className={`p-2 rounded-lg transition-colors ${
                    index === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={t('timelineSteps.moveUp')}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                
                {/* Move Down Button */}
                <button
                  type="button"
                  onClick={() => moveStepDown(index)}
                  disabled={index === (formData.timeline?.length || 0) - 1}
                  className={`p-2 rounded-lg transition-colors ${
                    index === (formData.timeline?.length || 0) - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={t('timelineSteps.moveDown')}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {/* Delete Button */}
              <button
                type="button"
                onClick={() => removeTimelineStep(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t('timelineSteps.removeStep')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              </div>
            </div>

            {/* Step Content - Hidden when collapsed */}
            {!isStepCollapsed(step.id || `step-${index}`) && (
              <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('timelineSteps.stepName')}
                </label>
                <input
                  type="text"
                  value={step.name}
                  onChange={(e) => updateTimelineStep(index, 'name', e.target.value)}
                  placeholder={t('timelineSteps.enterStepName')}
                  className="input-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('timelineSteps.duration')} (minutes)
                </label>
                <input
                  type="number"
                  value={step.minutes}
                  onChange={(e) => updateTimelineStep(index, 'minutes', parseInt(e.target.value) || 5)}
                  min="1"
                  className="input-field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('timelineSteps.stepType')}
                </label>
                <select
                  value={step.step_type}
                  onChange={(e) => updateTimelineStep(index, 'step_type', e.target.value)}
                  className="input-field"
                >
                  {stepTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Teacher Script */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('timelineSteps.teacherScript')}
                </h4>
                <button
                  type="button"
                  onClick={() => addTeacherScript(index)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-3 h-3" />
                  <span>{t('timelineSteps.addScript')}</span>
                </button>
              </div>
              <div className="space-y-3">
                {step.teacher_script?.map((script, scriptIndex) => (
                  <div key={`script-${index}-${scriptIndex}`} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">
                        {t('timelineSteps.scriptSegment')} {scriptIndex + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeTeacherScript(index, scriptIndex)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('timelineSteps.removeScript')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <RichTextEditor
                      value={script}
                      onChange={(value) => updateTeacherScript(index, scriptIndex, value)}
                      placeholder={t('timelineSteps.enterTeacherScript')}
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Student Actions */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('timelineSteps.studentActions')}
                </h4>
                <button
                  type="button"
                  onClick={() => addStudentAction(index)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-3 h-3" />
                  <span>{t('timelineSteps.addAction')}</span>
                </button>
              </div>
              <div className="space-y-2">
                {step.student_actions?.map((action, actionIndex) => (
                  <div key={`action-${index}-${actionIndex}`} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={action}
                      onChange={(e) => updateStudentAction(index, actionIndex, e.target.value)}
                      placeholder={t('timelineSteps.enterStudentAction')}
                      className="input-field flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeStudentAction(index, actionIndex)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Checks for Understanding */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('timelineSteps.checksForUnderstanding')}
                </h4>
                <button
                  type="button"
                  onClick={() => addCheckForUnderstanding(index)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-3 h-3" />
                  <span>{t('timelineSteps.addCheck')}</span>
                </button>
              </div>
              <div className="space-y-3">
                {step.checks_for_understanding?.map((check, checkIndex) => (
                  <div key={`check-${index}-${checkIndex}`} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('timelineSteps.checkType')}
                        </label>
                        <select
                          value={check.type || 'Observation'}
                          onChange={(e) => updateCheckForUnderstanding(index, checkIndex, 'type', e.target.value)}
                          className="input-field text-sm"
                        >
                          <option value="Observation">{t('timelineSteps.observation')}</option>
                          <option value="Question">{t('timelineSteps.question')}</option>
                          <option value="Quick Poll">{t('timelineSteps.quickPoll')}</option>
                          <option value="Exit Ticket">{t('timelineSteps.exitTicket')}</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('timelineSteps.prompt')}
                        </label>
                        <input
                          type="text"
                          value={check.prompt || ''}
                          onChange={(e) => updateCheckForUnderstanding(index, checkIndex, 'prompt', e.target.value)}
                          placeholder={t('timelineSteps.enterCheckForUnderstanding')}
                          className="input-field text-sm"
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('timelineSteps.lookFor')}
                      </label>
                      <input
                        type="text"
                        value={check.look_for || ''}
                        onChange={(e) => updateCheckForUnderstanding(index, checkIndex, 'look_for', e.target.value)}
                        placeholder={t('timelineSteps.enterLookFor')}
                        className="input-field text-sm"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('timelineSteps.commonError')}
                      </label>
                      <input
                        type="text"
                        value={check.common_error || ''}
                        onChange={(e) => updateCheckForUnderstanding(index, checkIndex, 'common_error', e.target.value)}
                        placeholder={t('timelineSteps.enterCommonError')}
                        className="input-field text-sm"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeCheckForUnderstanding(index, checkIndex)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div>
              <div className="mb-2">
                <h4 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                  <Monitor className="w-4 h-4 mr-2" />
                  {t('timelineSteps.resources')}
                </h4>
                
                {/* Add Resource Dropdown */}
                <div className="flex items-center space-x-2 mb-3">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        addResourceReference(index, e.target.value);
                        e.target.value = ''; // Reset selection
                      }
                    }}
                    className="input-field flex-1"
                    defaultValue=""
                  >
                    <option value="">{t('timelineSteps.selectResource')}</option>
                    {Object.entries(formData.resources || {}).map(([resourceId, resource]) => (
                      <option key={resourceId} value={resourceId}>
                        {resource.type} ({resourceId})
                      </option>
                    ))}
                  </select>
                </div>
                
                {Object.keys(formData.resources || {}).length === 0 && (
                  <p className="text-sm text-amber-600 mb-3">
                    {t('timelineSteps.noResourcesAvailable')}
                  </p>
                )}
              </div>
              
              {step.resources && step.resources.length > 0 ? (
                <div className="space-y-2">
                  {step.resources.map((stepResource, resourceIndex) => {
                    const resource = formData.resources?.[stepResource.resourceId];
                    return (
                      <div key={`resource-${index}-${resourceIndex}`} className="flex items-center justify-between p-2 border border-gray-200 rounded bg-gray-50">
                        <div className="flex items-center space-x-2 flex-1">
                          {/* Resource Order Controls */}
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveResourceUp(index, resourceIndex)}
                              disabled={resourceIndex === 0}
                              className={`p-0.5 rounded transition-colors ${
                                resourceIndex === 0
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-600 hover:bg-gray-200'
                              }`}
                              title={t('timelineSteps.moveResourceUp')}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveResourceDown(index, resourceIndex)}
                              disabled={resourceIndex === (step.resources?.length || 0) - 1}
                              className={`p-0.5 rounded transition-colors ${
                                resourceIndex === (step.resources?.length || 0) - 1
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-600 hover:bg-gray-200'
                              }`}
                              title={t('timelineSteps.moveResourceDown')}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                          
                          <Monitor className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            {resource?.type || 'Unknown'}
                          </span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {stepResource.config?.resourceId || stepResource.resourceId}
                          </span>
                          {stepResource.config && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              {t('timelineSteps.customConfig')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => openConfigModal(index, resourceIndex)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={t('timelineSteps.configureResource')}
                          >
                            <Settings className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeResourceReference(index, resourceIndex)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={t('timelineSteps.removeResource')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-2 text-gray-500 text-sm">
                  {t('timelineSteps.noResourcesAttached')}
                </div>
              )}
            </div>
              </>
            )}
          </motion.div>
        ))}
        
        <button
          type="button"
          onClick={addTimelineStep}
          className="btn-secondary flex items-center space-x-2 w-full"
        >
          <Plus className="w-4 h-4" />
          <span>{t('timelineSteps.addStep')}</span>
        </button>
      </div>

      {/* Resource Configuration Modal */}
      <ResourceConfigModal
        key={`${selectedResource?.stepIndex}-${selectedResource?.resourceIndex}`}
        isOpen={isConfigModalOpen}
        onClose={closeConfigModal}
        onSave={handleConfigSave}
        resource={selectedResource?.resource || null}
        currentConfig={selectedResource?.currentConfig}
      />
      

    </div>
  );
};
